/* eslint-disable import/prefer-default-export */

const axios = require('axios').default;
import { toast } from 'react-toastify';

import {
  offsetDateForServer,
  toCalendarDate
} from '../../../shared/timeZoneLogic';

import {
  CLOSE_MODAL,
  CREATE_CALENDAR_EVENT_CHANGED,
  EVENT_NAME_CHANGED,
  VENUE_ID_CHANGED,
  START_TIME_CHANGED,
  PRIMARY_ARTIST_CHANGED,
  CALENDAR_EVENT_CHANGED,
  PERFORMERS_CHANGED,
  PERFORMER_ID_CHANGED,
  SELECTED_ARTIST_CHANGED,
  CLEAR_SELECTED_ARTIST,
  EVENT_TEMPLATE_CHANGED,
  SELECTED_ROOMS_CHANGED,
  AGE_RESTRICTION_CHANGED,
} from '../constants/offerV2ModalConstants';

export const buildPrimaryPerformerAttributes = (primaryArtist, extra = {}) => {
  if (!primaryArtist) { return null; }
  return Object.assign({ artistable_type: "Artist", artistable_id: primaryArtist.value }, extra);
};

export const closeModal = (dispatch, onClose) => {
  return dispatch => {
    onClose();
    dispatch(modalWasClosed());
  };
};

export const modalWasClosed = () => ({
  type: CLOSE_MODAL
});

export const createCalendarEventChanged = (createCalendarEvent) => ({
  type: CREATE_CALENDAR_EVENT_CHANGED,
  createCalendarEvent
});

export const eventNameChanged = (name) => ({
  type: EVENT_NAME_CHANGED,
  name
});

export const eventTemplateChanged = (eventTemplate) => ({
  type: EVENT_TEMPLATE_CHANGED,
  eventTemplate
});

export const venueIdChanged = (venueId) => ({
  type: VENUE_ID_CHANGED,
  venueId
});

export const startTimeChanged = (date) => ({
  type: START_TIME_CHANGED,
  date
});

export const primaryArtistChanged = (artist) => ({
  type: PRIMARY_ARTIST_CHANGED,
  artist
});

export const calendarEventChanged = (calendarEvent) => ({
  type: CALENDAR_EVENT_CHANGED,
  calendarEvent
});

export const fetchCalendarEventPerformers = (dispatch, team, calendarEventId) => {
  return dispatch => {
    return axios.get("/teams/" + team.id + "/performers", {
        params: {
          performable_type: "CalendarEvent",
          performable_id: calendarEventId
        }
      })
      .then(({ data }) => {
        dispatch(performersChanged(data));
      })
  };
};

export const performersChanged = (performers) => ({
  type: PERFORMERS_CHANGED,
  performers
});

export const performerIdChanged = (performerId) => ({
  type: PERFORMER_ID_CHANGED,
  performerId
});

export const fetchArtistData = (dispatch, artistId) => {
  return dispatch => {
    return axios.get("/artists/" + artistId + ".json")
      .then(({ data }) => {
        dispatch(selectedArtistChanged(data));
      })
  };
};

export const selectedArtistChanged = (artist) => ({
  type: SELECTED_ARTIST_CHANGED,
  artist
});

export const clearSelectedArtist = () => ({
  type: CLEAR_SELECTED_ARTIST
});

export const selectedRoomsChanged = (selectedRooms) => ({
  type: SELECTED_ROOMS_CHANGED,
  selectedRooms
});

export const ageRestrictionChanged = (ageRestriction) => ({
  type: AGE_RESTRICTION_CHANGED,
  ageRestriction
});

export const createOffer = (dispatch, csrfToken, team, offer, pipeline, offerV3, layout) => {
  return dispatch => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    if (offerV3) {
      return createV3Offer(csrfToken, team, offer, layout);
    }

    var offerParams = {
      ui_version: 2,
      should_create_calendar_event: offer.create_calendar_event,
    };

    if(pipeline && pipeline.id){
      offerParams.update_pipeline_attributes = {
        id: pipeline.id,
        state: "offer"
      };
    } else {
      offerParams.pipeline_attributes = {
        team_id: team.id,
        state: "offer"
      };
    }

    if(offer.create_calendar_event){
      const ppa = buildPrimaryPerformerAttributes(offer.primary_artist);
      if (ppa) { offerParams.primary_performer_attributes = ppa; }

      if(offer.start_time && offer.start_time.length > 0){
        // start_date is only a presence guard on this path -- Offer's
        // set_attributes_from_calendar_event reassigns it from the calendar event
        // unconditionally (unlike V3Offer's ||=), so the value never persists.
        // Sent as a calendar date anyway so both paths read the same.
        offerParams.start_date = toCalendarDate(offer.start_time[0]);
        offerParams.start_time = offsetDateForServer(offer.start_time[0]);
      }

      offerParams = Object.assign({}, offerParams, {
        age_restriction: offer.age_restriction,
        venue_id: offer.venue_id,
        room_ids: offer.room_ids || [],
        event_name: offer.event_name,
        event_template_id: offer.event_template?.value
      });
    } else {
      offerParams = Object.assign({}, offerParams, {
        calendar_event_id: offer.calendar_event_id,
        performer_id: offer.performer_id
      });
    }

    return axios.post("/teams/" + team.id + "/offers", {
        offer: offerParams
      })
      .then(({ data }) => {
        window.location.href = ("/teams/" + team.id + "/offers/" + data.id + "/edit");
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };
};

const createV3Offer = (csrfToken, team, offer, layout) => {
  var v3Params = {
    should_create_calendar_event: offer.create_calendar_event,
  };

  if(offer.create_calendar_event){
    const ppa = buildPrimaryPerformerAttributes(offer.primary_artist, { act_type: "headline" });
    if (ppa) { v3Params.primary_performer_attributes = ppa; }

    if(offer.start_time && offer.start_time.length > 0){
      v3Params.start_date = toCalendarDate(offer.start_time[0]);
      v3Params.start_time = offsetDateForServer(offer.start_time[0]);
    }

    v3Params = Object.assign({}, v3Params, {
      age_restriction: offer.age_restriction,
      venue_id: offer.venue_id,
      room_ids: offer.room_ids || [],
      event_name: offer.event_name,
      event_template_id: offer.event_template?.value
    });
  } else {
    v3Params = Object.assign({}, v3Params, {
      calendar_event_id: offer.calendar_event_id,
      performer_id: offer.performer_id
    });
  }

  v3Params.editor_variant = layout === "simple" ? "v3_simple" : "v3";

  return axios.post("/teams/" + team.id + "/offers/v3", {
      v3_offer: v3Params
    })
    .then(({ data }) => {
      // "simple" layout opens the stripped-down editor; "advanced" (default)
      // opens the full V3 builder. For a brand-new simple offer, ?edit=1 drops
      // the user straight onto the Create Artist Deal form for the artist they
      // just selected, instead of the lineup view.
      window.location.href = layout === "simple"
        ? "/teams/" + team.id + "/offers/v3_simple/" + data.id + "/edit?edit=1"
        : "/teams/" + team.id + "/offers/v3/" + data.id + "/edit?from=offers";
    })
    .catch((error) => {
      toast.error(error.response.data.join(", "), {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    });
};
